import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '../config/firebase.js'

// Statuses that mean the patent has already been filed with the USPTO. Kept in
// sync with the display logic in EditPatent/Dashboard.
const FILED_STATUSES = new Set(['filed', 'filed-to-uspto', 'completed'])

/** True if the patent doc data represents an already-filed patent. */
export function isPatentFiled(data) {
  return FILED_STATUSES.has(data?.status) || FILED_STATUSES.has(data?.applicationStatus)
}

/** Best-effort millis from a Firestore Timestamp | Date | number | ISO string. */
function toMillis(ts) {
  if (!ts) return 0
  if (typeof ts.toMillis === 'function') return ts.toMillis()
  if (ts instanceof Date) return ts.getTime()
  const n = typeof ts === 'number' ? ts : Date.parse(ts)
  return Number.isNaN(n) ? 0 : n
}

/**
 * Turn a YYYY-MM-DD value from a date input into a Date at local noon.
 *
 * Noon, not midnight, and local, not UTC: `new Date('2026-08-04')` parses as UTC midnight,
 * which is the 3rd of August in every timezone west of Greenwich. A filing date that shifts
 * by a day depending on where the user is sitting is not a filing date.
 */
function dateFromInput(value) {
  if (typeof value !== 'string') return null
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  const [, y, m, d] = match
  const date = new Date(Number(y), Number(m) - 1, Number(d), 12, 0, 0)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Mark a patent as filed to the USPTO. Flips the patent to `filed` immediately.
 * If a receipt is provided (uploading the receipt IS the confirmation), its
 * details are recorded too; otherwise the patent is filed without a receipt on
 * record (for users who filed but no longer have the receipt).
 *
 * `filedOn` is the date the user actually filed, which is NOT the date they told us. Someone
 * who filed three weeks ago and is only now uploading the receipt was previously recorded as
 * having filed today — and the twelve-month provisional deadline runs from the real date, so
 * that error is the one that can cost somebody their priority date. It is stored separately
 * from `filedRecordedAt`, which is when we were told: conflated in one field, as they were,
 * the two facts cannot be separated again afterwards.
 *
 * @param {string} uid
 * @param {string} patentId
 * @param {{url: string, path: string, name: string} | null} [receipt]
 * @param {string | null} [filedOn] YYYY-MM-DD. Falls back to now when absent or unparseable.
 */
export async function markPatentFiled(uid, patentId, receipt = null, filedOn = null) {
  const patentRef = doc(db, 'users', uid, 'patents', patentId)
  const filedDate = dateFromInput(filedOn)
  // A filing cannot have happened tomorrow. A typo in a date field should not move a
  // deadline into the future.
  const filedWhen = filedDate && filedDate <= new Date() ? filedDate : serverTimestamp()
  const update = {
    applicationStatus: 'filed-to-uspto',
    status: 'filed',
    filedAt: filedWhen,
    dateFiled: filedWhen,
    // When we were told, always. The audit fact, kept apart from the filing fact.
    filedRecordedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    step: 'filed-to-uspto',
  }
  if (receipt) {
    update.filingReceiptUrl = receipt.url
    update.filingReceiptPath = receipt.path
    update.filingReceiptName = receipt.name
    update.filingReceiptUploadedAt = serverTimestamp()
    update.filedWithoutReceipt = false
  } else {
    // No receipt — user self-attested that they filed. Keep a record of it.
    update.filedWithoutReceipt = true
    update.filedWithoutReceiptAt = serverTimestamp()
  }
  await setDoc(patentRef, update, { merge: true })
}

/**
 * Return the user's paid (unlocked) patents that still need a filing
 * confirmation: paid the $320 unlock, not yet filed, and no receipt on file.
 * Sorted most-recent first. Used by the recurring filing-reminder popup.
 *
 * @param {string} uid
 * @returns {Promise<Array<{patentId: string} & Record<string, any>>>}
 */
export async function getUnfiledPaidPatents(uid) {
  if (!uid) return []

  const userSnap = await getDoc(doc(db, 'users', uid))
  const unlocked = userSnap.exists() ? userSnap.data().unlockedPatents || [] : []
  if (!Array.isArray(unlocked) || unlocked.length === 0) return []

  const patents = await Promise.all(
    unlocked.map(async (patentId) => {
      try {
        const snap = await getDoc(doc(db, 'users', uid, 'patents', patentId))
        if (!snap.exists()) return null
        const data = snap.data()
        if (isPatentFiled(data)) return null
        if (data.filingReceiptUrl) return null
        // Only patents that have finished generating — never nag mid-generation.
        if (!data.generatedSpecification) return null
        return { patentId, ...data }
      } catch (err) {
        console.error(`[filing] failed to read patent ${patentId}:`, err)
        return null
      }
    }),
  )

  return patents
    .filter(Boolean)
    .sort((a, b) => toMillis(b.updatedAt || b.createdAt) - toMillis(a.updatedAt || a.createdAt))
}
